import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Smartphone,
  Bell,
  MessageCircle,
  ArrowRight,
  CalendarCheck,
  Star,
  CheckCircle2,
  Apple,
} from "lucide-react";
import Button from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { cn } from "../lib/utils";

const HIGHLIGHTS = [
  {
    icon: MessageCircle,
    title: "Book from WhatsApp",
    description: "No app required to book, confirm, or get reminders — it all works where you already chat.",
  },
  {
    icon: CalendarCheck,
    title: "Never miss a slot",
    description: "Smart reminders and one-tap rescheduling keep your appointments right on time.",
  },
  {
    icon: Bell,
    title: "Get notified first",
    description: "Leave your email and we'll let you know the moment the app is ready to download.",
  },
];

// Screenshots live in public/screenshots/. Make them with /screenshot-maker.html
// ("shuwa.app" tab) so they come out framed and sized consistently.
const GALLERY = [
  { file: "download-1.png", title: "Skip the wait", description: "Your booking is your turn.", alt: "The SHUWA app introduction screen" },
  { file: "download-2.png", title: "Pick a time", description: "See open slots and choose one.", alt: "Choosing a date and time for a booking in the SHUWA app" },
  { file: "download-3.png", title: "Stay on top of bookings", description: "Cancel or say you're running late.", alt: "Upcoming appointments in the SHUWA app" },
  { file: "download-4.png", title: "Gift cards", description: "Treat someone to a fresh look.", alt: "Gift cards in the SHUWA app" },
  { file: "download-5.png", title: "Run your business", description: "Today's bookings and revenue.", alt: "The SHUWA business dashboard with today's appointments" },
];

export default function DownloadApp() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email address.");
      return;
    }
    setError("");
    setSubmitted(true);
  };

  return (
    <div className="overflow-hidden">
      {/* Hero */}
      <section className="relative bg-slate-900 text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 left-1/2 h-[600px] w-[600px] -translate-x-1/2 rounded-full bg-primary/25 blur-[140px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-0 right-0 h-[380px] w-[380px] translate-x-1/3 translate-y-1/3 rounded-full bg-accent/20 blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(circle,white_1px,transparent_1px)] [background-size:28px_28px]"
        />

        <div className="relative mx-auto grid max-w-6xl gap-12 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-2 lg:items-center lg:py-28">
          <div className="text-center lg:text-left">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary-foreground/90 backdrop-blur">
              <Smartphone className="h-3.5 w-3.5 text-primary" />
              Coming soon
            </span>

            <h1 className="mt-5 font-montserrat text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              The SHUWA app is on its way.
            </h1>

            <p className="mx-auto mt-5 max-w-xl text-base text-slate-300 sm:text-lg lg:mx-0">
              We're putting the finishing touches on the SHUWA mobile app for iOS and Android.
              In the meantime, book, manage, and get reminders right from your browser or WhatsApp
              — no waiting required.
            </p>

            <div className="mt-6 flex items-center justify-center gap-4 lg:justify-start">
              <div className="flex -space-x-2">
                {["bg-primary", "bg-accent", "bg-fuchsia-400", "bg-sky-400"].map((c, i) => (
                  <span
                    key={i}
                    className={cn(
                      "h-8 w-8 rounded-full border-2 border-slate-900",
                      c
                    )}
                  />
                ))}
              </div>
              <div className="text-left text-sm text-slate-300">
                <div className="flex items-center gap-1 text-amber-400">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-current" />
                  ))}
                </div>
                <p>Loved by early businesses on the web</p>
              </div>
            </div>

            {/* Email capture */}
            <div className="mx-auto mt-8 max-w-md lg:mx-0">
              {submitted ? (
                <div className="flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-5 py-3 text-sm font-medium text-emerald-300">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  You're on the list — we'll email you the moment it's ready.
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  <div className="flex flex-col gap-2 rounded-full bg-white/5 p-1.5 ring-1 ring-white/15 backdrop-blur sm:flex-row">
                    <label htmlFor="notify-email" className="sr-only">
                      Email address
                    </label>
                    <input
                      id="notify-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full flex-1 rounded-full bg-transparent px-4 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none"
                    />
                    <Button type="submit" size="md" className="shrink-0 shadow-lg shadow-primary/30">
                      Notify me
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                  {error && <p className="mt-2 text-left text-xs text-red-400">{error}</p>}
                </form>
              )}
              <p className="mt-3 text-xs text-slate-400">
                Prefer not to wait? <Link to="/" className="font-semibold text-primary-foreground underline underline-offset-2 hover:text-white">Continue on the web</Link> — everything already works today.
              </p>
            </div>
          </div>

          {/* App screenshot */}
          <div className="relative mx-auto hidden w-full max-w-xs lg:block">
            <div
              aria-hidden
              className="absolute inset-0 -z-10 rounded-[3rem] bg-gradient-to-br from-primary/40 to-accent/30 blur-3xl"
            />
            <img
              src="/screenshots/download-hero.png"
              alt="The SHUWA app home screen with popular businesses near you"
              width="640"
              height="1316"
              className="relative mx-auto h-auto w-full drop-shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="mx-auto -mt-10 max-w-5xl px-4 pb-14 sm:px-6 sm:pb-24">
        <div className="grid gap-6 sm:grid-cols-3">
          {HIGHLIGHTS.map(({ icon: Icon, title, description }) => (
            <Card
              key={title}
              className="p-6 transition-transform hover:-translate-y-1 hover:shadow-lg sm:p-8"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-montserrat text-base font-bold text-slate-900">{title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{description}</p>
            </Card>
          ))}
        </div>

        {/* App screenshots */}
        <div className="mt-16 text-center">
          <h2 className="font-montserrat text-2xl font-bold text-slate-900 sm:text-3xl">
            A look inside the app
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
            Book, pay and manage appointments in a few taps. Businesses get their calendar and clients in the same app.
          </p>
          <div className="-mx-4 mt-8 flex snap-x gap-5 overflow-x-auto px-4 pb-4 [-ms-overflow-style:none] [scrollbar-width:none] lg:mx-0 lg:grid lg:grid-cols-5 lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden">
            {GALLERY.map(({ file, title, description, alt }) => (
              <figure key={file} className="w-44 shrink-0 snap-center lg:w-auto">
                <img
                  src={`/screenshots/${file}`}
                  alt={alt}
                  width="400"
                  height="822"
                  loading="lazy"
                  className="h-auto w-full drop-shadow-xl"
                />
                <figcaption className="mt-4">
                  <p className="font-montserrat text-sm font-bold text-slate-900">{title}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{description}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>

        {/* Store badges */}
        <div className="mt-14 flex flex-col items-center gap-5 text-center">
          <p className="text-sm font-medium text-muted-foreground">Available soon on</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <span className="inline-flex cursor-not-allowed items-center gap-2 rounded-xl border border-border bg-slate-900 px-4 py-2.5 text-white opacity-70">
              <Apple className="h-6 w-6" />
              <span className="text-left leading-tight">
                <span className="block text-[10px] text-slate-300">Download on the</span>
                <span className="block text-sm font-semibold">App Store</span>
              </span>
            </span>
            <span className="inline-flex cursor-not-allowed items-center gap-2 rounded-xl border border-border bg-slate-900 px-4 py-2.5 text-white opacity-70">
              <Smartphone className="h-6 w-6" />
              <span className="text-left leading-tight">
                <span className="block text-[10px] text-slate-300">Get it on</span>
                <span className="block text-sm font-semibold">Google Play</span>
              </span>
            </span>
          </div>

          <Button as={Link} to="/" size="lg" className="mt-4 shadow-lg shadow-primary/30">
            Continue on the web
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
